import { differenceInDays, subDays } from 'date-fns';
import { FunctionComponent, memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AnalyticChartInterval,
  TimeSeriesDataset,
  useUserActivityQuery,
} from '../../../../../services';
import { COLORS } from '../../Analytics';
import { ChartWrapper, Loader, StatsItem, TimeSeriesChart } from '../../helpers';
import { UserProps } from '../User';

import classes from '../../Analytics.module.scss';

const TYPE_LOCALISE_KEY = 'chartTypes.user-activity-rate';

export const UserActivityRate: FunctionComponent<UserProps> = memo(({ from, to, platform }) => {
  const { t } = useTranslation();

  const { data: userActivity, isFetching: isFetchingUserActivity } = useUserActivityQuery({
    from,
    to,
    interval: AnalyticChartInterval.AUTO,
    platform,
  });

  const { data: userActivityStats, isFetching: isFetchingUserActivityStats } = useUserActivityQuery(
    { from, to, platform }
  );

  const daysDiff = differenceInDays(new Date(to), new Date(from)) + 1;

  const { data: userActivityStatsPrev, isFetching: isFetchingUserActivityStatsPrev } =
    useUserActivityQuery({
      from: subDays(new Date(from), daysDiff).toISOString(),
      to: subDays(new Date(to), daysDiff).toISOString(),
      platform,
    });

  const convertActivityToRate = useCallback(
    (datasets: TimeSeriesDataset[]): TimeSeriesDataset[] => {
      const activeArr = datasets.find(({ label }) => label === 'active')?.data ?? [];

      const engagedArr = datasets.find(({ label }) => label === 'engaged')?.data ?? [];

      return [
        {
          data: engagedArr.map((engaged, index) => {
            const active = activeArr[index];

            if (!engaged || !active) {
              return 0;
            }

            return Number(((engaged / active) * 100).toFixed(2));
          }),
          label: 'rate',
        },
      ];
    },
    []
  );

  const renderStats = useMemo(() => {
    if (isFetchingUserActivityStats || isFetchingUserActivityStatsPrev) {
      return <Loader stats />;
    }

    if (!userActivityStats || !userActivityStatsPrev) {
      return null;
    }

    const datasets = convertActivityToRate(userActivityStats.datasets);

    const datasetsPrev = convertActivityToRate(userActivityStatsPrev.datasets);

    return (
      <div className={classes['analytics__chart-stats']}>
        {datasets.map(({ label, data }, index) => {
          return (
            <StatsItem
              key={label}
              label={label}
              localiseKey={'userActivityRateLabels.rate'}
              hintLocaliseKey={'userActivityRateHints.rate'}
              color={COLORS[index]}
              value={data[0] ?? 0}
              valuePrev={datasetsPrev.find((set) => set.label === label)?.data[0] ?? 0}
              valueFormatter={(value) => `${value}%`}
              isInline
            />
          );
        })}
      </div>
    );
  }, [
    convertActivityToRate,
    isFetchingUserActivityStats,
    isFetchingUserActivityStatsPrev,
    userActivityStats,
    userActivityStatsPrev,
  ]);

  return (
    <ChartWrapper typeLocaliseKey={TYPE_LOCALISE_KEY} infoLocaliseKey={'userActivityRate.info'}>
      {renderStats}
      {userActivity && (
        <TimeSeriesChart
          timeSeries={{ ...userActivity, datasets: convertActivityToRate(userActivity.datasets) }}
          labels={['rate']}
          localiseKeyLabels={'userActivityRateLabels'}
          loading={isFetchingUserActivity}
          ariaLabel={t(TYPE_LOCALISE_KEY)}
          valueFormatter={(value) => `${value}%`}
        />
      )}
    </ChartWrapper>
  );
});
